/**
 * The other people to reach on an account.
 *
 * The sheet carries one contact — a person and a number — and collectors ring
 * more than one: the accounts clerk, the owner, the person who signs cheques.
 * These are the company's own record, entered in the product (the sheet mapping
 * says so), stored in `customers.additional_contacts`, and never touched by a
 * sync.
 *
 * The rules here mirror `valid_contacts()` in migration 0033, which the table
 * itself enforces. They exist so a person is told what is wrong before the
 * database refuses it; they are not the boundary.
 */

export interface ContactPerson {
    name: string;
    phone: string;
    /** What they do: accounts, owner, purchase. */
    role: string;
    email: string;
}

export const MAX_CONTACTS = 20;

export const CONTACT_LIMITS = { name: 120, phone: 40, role: 80, email: 200 } as const;

/** Roles people type most, offered as suggestions; any other word is fine. */
export const CONTACT_ROLE_SUGGESTIONS = ['Accounts', 'Owner', 'Purchase', 'Manager', 'Cheque signatory'] as const;

export const emptyContact = (): ContactPerson => ({ name: '', phone: '', role: '', email: '' });

/**
 * The number a press of Call dials, from a phone field that may hold several
 * ("98140 12345, 0172-2654321 (office)" — a TallyPrime ledger's phone field
 * often does): the first part of six or more digits, with its leading +, and
 * nothing else of the field. Null when no part is a number.
 */
export function dialableNumber(field: string): string | null {
    for (const part of field.split(/[,;/|]|\s{2,}/)) {
        const digits = part.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');
        if (digits.replace(/\D/g, '').length >= 6) return digits;
    }
    return null;
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

/**
 * Whatever came back from the database, as contacts.
 *
 * Tolerant on reading — an unexpected value is skipped rather than breaking the
 * account's page — because the table's check guarantees the shape of anything
 * written since 0033.
 */
export function readContacts(value: unknown): ContactPerson[] {
    if (!Array.isArray(value)) return [];
    return value
        .filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null && !Array.isArray(item))
        .map(item => ({
            name: text(item['name']),
            phone: text(item['phone']),
            role: text(item['role']),
            email: text(item['email']),
        }));
}

/** Trimmed, with empty fields left out, as the database stores it. */
export function contactsForSaving(contacts: readonly ContactPerson[]): Record<string, string>[] {
    return contacts.map(contact => {
        const out: Record<string, string> = {};
        for (const key of ['name', 'phone', 'role', 'email'] as const) {
            const value = contact[key].trim();
            if (value) out[key] = value;
        }
        return out;
    });
}

export interface ContactProblem {
    index: number;
    field: keyof ContactPerson | 'list';
    message: string;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Everything wrong with a list, so it can all be fixed at once. */
export function contactProblems(contacts: readonly ContactPerson[]): ContactProblem[] {
    const problems: ContactProblem[] = [];
    if (contacts.length > MAX_CONTACTS) {
        problems.push({ index: -1, field: 'list', message: `An account can list at most ${MAX_CONTACTS} other people.` });
    }
    contacts.forEach((contact, index) => {
        const phone = contact.phone.trim();
        const email = contact.email.trim();
        if (!phone && !email) {
            problems.push({ index, field: 'phone', message: 'Give a phone number or an email.' });
        }
        if (email && !EMAIL.test(email)) {
            problems.push({ index, field: 'email', message: 'That does not look like an email address.' });
        }
        if (phone && !/\d/.test(phone)) {
            problems.push({ index, field: 'phone', message: 'A phone number needs digits.' });
        }
        for (const key of ['name', 'phone', 'role', 'email'] as const) {
            if (contact[key].trim().length > CONTACT_LIMITS[key]) {
                problems.push({ index, field: key, message: `Keep this under ${CONTACT_LIMITS[key]} characters.` });
            }
        }
    });
    return problems;
}

/**
 * Who a reminder can go to: the sheet's contact first, then the people listed
 * in the product, each with a usable number or address.
 */
export interface ReminderRecipient {
    key: string;
    label: string;
    phone: string;
    email: string;
}

export function reminderRecipients(
    primary: { contactPerson?: string; contactNumber?: string; email?: string | undefined },
    others: readonly ContactPerson[],
): ReminderRecipient[] {
    const list: ReminderRecipient[] = [];
    if ((primary.contactNumber ?? '').trim() || (primary.email ?? '').trim()) {
        list.push({
            key: 'primary',
            label: primary.contactPerson?.trim() ? `${primary.contactPerson.trim()} (from the sheet)` : 'The number from the sheet',
            phone: (primary.contactNumber ?? '').trim(),
            email: (primary.email ?? '').trim(),
        });
    }
    others.forEach((contact, index) => {
        const name = contact.name.trim() || 'Unnamed contact';
        list.push({
            key: `other-${index}`,
            label: contact.role.trim() ? `${name} (${contact.role.trim()})` : name,
            phone: contact.phone.trim(),
            email: contact.email.trim(),
        });
    });
    return list;
}
